import React from 'react';

import { SafeHtml } from '@bcp-channels-sharedcomponents-reactjs/core';
import {
	BaseModal,
	Box,
	Button,
	CSSProperties,
	Image,
	Overlay,
	Typography,
	TypographyAppearance,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { NavClose } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { BaseSkeleton } from '@components';
import { useLoadingStore, useSessionInfo } from '@store';
import { useTheme } from '@themes';
import { BaseSessionInfoLoanType } from '@types';

import { useAutoSimulation } from './autoSimulate.helper';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AutoSimulateModalAb: React.FC = () => {
	const { currentView, handleClickContinueApp, handleOnClickClose, isVisible, qrCodeToApp, translate } =
		useAutoSimulation();
	const sessionInfo = useSessionInfo();
	const creditType = sessionInfo?.journeyData.loanType || 'AL';
	const { images } = useTheme();
	const { isLoadingQRCodeAndLink } = useLoadingStore();

	const simulateStyles = {
		smallView: {
			modalConfig: {
				top: '50%',
				left: '50%',
				maxHeight: '90vh',
				transform: 'translate(-50%, -50%)',
				position: 'fixed',
				padding: undefined,
				height: undefined,
				width: 'calc(100vw - 32px)',
				flexDirection: 'column',
			},
			contentConfig: {
				padding: '32px',
				marginLeft: undefined,
			},
			imageBoxConfig: {
				alignSelf: 'end',
				maxWidth: '227px',
			},
			imageConfig: {
				width: '100%',
				height: '227px',
			},
			titleBoxConfig: {
				marginTop: '8px',
				textAlign: 'center',
			},
			titleConfig: { appearance: 'subtitle-100-02' as TypographyAppearance },
			logoBoxConfig: {},
			buttonSkeleton: {
				width: '100%',
				height: '48px',
			},
		},
		tabletView: {
			modalConfig: {
				maxHeight: '90vh',
				width: 'calc(100vw - 32px)',
				maxWidth: '456px',
				padding: '32px 32px 24px 32px',
				flexDirection: 'column',
			},
			contentConfig: {
				paddingBottom: '32px',
				marginLeft: '8px',
			},
			imageBoxConfig: {
				alignSelf: 'end',
			},
			imageConfig: {
				width: '242px',
				height: '100%',
			},
			titleBoxConfig: {
				marginTop: '8px',
				textAlign: 'center',
			},
			titleConfig: { appearance: 'headline-75-01' as TypographyAppearance },
			logoBoxConfig: {
				marginTop: '22px',
			},
			buttonSkeleton: {
				width: '100%',
				height: '48px',
			},
		},
		bigView: {
			modalConfig: {
				height: undefined,
				width: '632px',
				padding: '32px',
				flexDirection: 'row',
			},
			contentConfig: {
				marginLeft: '8px',
			},
			imageBoxConfig: {
				alignSelf: 'end',
			},
			imageConfig: {},
			titleBoxConfig: {
				width: '103%',
				textAlign: 'center',
			},
			titleConfig: { appearance: 'headline-100-04' as TypographyAppearance },
			logoBoxConfig: {
				marginTop: '22px',
			},
			qrCodeImage: {
				width: '161px',
				height: '161px',
			},
		},
	};

	return (
		<BaseModal visible={isVisible} externalBackdrop={<Overlay onPress={handleOnClickClose} />}>
			<Box
				styles={{
					background: '#FFFFFF',
					borderRadius: '24px',
					flexDirection: 'column',
				}}
			>
				<Box
					styles={{
						background: '#FFFFFF',
						borderRadius: '24px',
						...(simulateStyles[currentView].modalConfig as CSSProperties),
					}}
				>
					<Box
						styles={{
							alignItems: 'center',
							flexDirection: 'column',
							flex: 1,
							...simulateStyles[currentView].contentConfig,
						}}
					>
						{currentView !== 'bigView' && (
							<Box onPress={handleOnClickClose} styles={{ zIndex: 3, alignSelf: 'end' }}>
								<Image
									source={NavClose.Outline}
									svgProps={{
										fill: '#6A717C',
										width: '24px',
										height: '24px',
									}}
								/>
							</Box>
						)}
						<Box styles={{ alignSelf: 'center', ...simulateStyles[currentView].logoBoxConfig }}>
							<Image source={images.abLogo.src} />
						</Box>
						<Box styles={{ ...(simulateStyles[currentView].titleBoxConfig as CSSProperties) }}>
							<Typography appearance={simulateStyles[currentView].titleConfig.appearance}>
								{BaseSessionInfoLoanType.CL === creditType
									? translate('simulator.modals.autoSimulate.onlinePersonalLoanIsExclusive')
									: BaseSessionInfoLoanType.AL === creditType
									? translate('simulator.modals.autoSimulate.onlineUsedCardLoanIsExclusive')
									: BaseSessionInfoLoanType.ALEV === creditType
									? translate('simulator.modals.autoSimulate.onlineUsedCardLoanIsExclusiveALEV')
									: BaseSessionInfoLoanType.HOME === creditType
									? translate('simulator.modals.autoSimulate.onlineUsedCardLoanIsExclusiveHOME')
									: translate('simulator.modals.autoSimulate.onlineRenewableEnergyLoanIsExclusive')}
							</Typography>
						</Box>
						{currentView === 'bigView' && (
							<>
								<Box styles={{ marginTop: '8px', alignSelf: 'center' }}>
									<Typography appearance="body-200-01">
										<SafeHtml
											html={translate('simulator.modals.autoSimulate.readQRCodeToInstallTheApp')}
										/>
									</Typography>
								</Box>

								<Box
									styles={{
										alignSelf: 'center',
										marginTop: '32px',
									}}
								>
									{isLoadingQRCodeAndLink ? (
										<BaseSkeleton height="161px" width="161px" />
									) : (
										<Image
											styles={simulateStyles[currentView].qrCodeImage}
											source={`data:image/jpeg;base64, ${qrCodeToApp}`}
											alt="qr-code"
										/>
									)}
								</Box>
							</>
						)}
					</Box>
					{currentView !== 'bigView' && (
						<Box styles={{ paddingBottom: '32px', flexDirection: 'column' }}>
							<Box styles={{ alignSelf: 'center', width: '80%' }}>
								{isLoadingQRCodeAndLink ? (
									<BaseSkeleton
										width={simulateStyles[currentView].buttonSkeleton.width}
										height={simulateStyles[currentView].buttonSkeleton.height}
									/>
								) : (
									<Button
										onPress={handleClickContinueApp}
										size={'large'}
										fullWidth
										children={translate('simulator.modals.autoSimulate.continueOnTheApp')}
									/>
								)}
							</Box>
						</Box>
					)}
					{currentView === 'bigView' && (
						<>
							{/* <Box
									styles={{
										alignItems: 'center',
										flexDirection: 'column',
										flex: 1,
										alignSelf: 'center',
										marginLeft: currentView === 'bigView' ? '-50px' : undefined,
										marginTop: currentView === 'bigView' ? '0px' : '60px',
										marginRight: currentView === 'tabletView' ? '-20px' : undefined,
									}}
								> */}
							{/* <Image
										svgProps={{ ...simulateStyles[currentView].imageConfig }}
										source={
											BaseSessionInfoLoanType.AL === creditType
												? sessionInfo.language === BaseSessionInfoLanguage.EN
													? currentView === 'bigView'
														? images.autoModalEn.src
														: images.autoModalMobileEn.src
													: currentView === 'bigView'
													? images.autoModalPt.src
													: images.autoModalMobilePt.src
												: BaseSessionInfoLoanType.CL === creditType
												? sessionInfo.language === BaseSessionInfoLanguage.EN
													? images.personalModalEn.src
													: images.personalModalPt.src
												: sessionInfo.language === BaseSessionInfoLanguage.EN
												? currentView === 'bigView'
													? images.renenModalEn.src
													: images.renenModalMobileEn.src
												: currentView === 'bigView'
												? images.renenModalPt.src
												: images.renenModalMobilePt.src
										}
									/> */}
							{/* <Image
										svgProps={{ ...simulateStyles[currentView].imageConfig }}
										source={
											currentView === 'bigView'
												? images.genericModalDesktop.src
												: images.genericModalMobile.src
										}
									/> */}
							{/* </Box> */}
							<Box onPress={handleOnClickClose} styles={{ marginLeft: '-16px', zIndex: 3 }}>
								<Image
									source={NavClose.Outline}
									svgProps={{
										fill: '#6A717C',
										width: '24px',
										height: '24px',
									}}
								/>
							</Box>
						</>
					)}
				</Box>
				{/* {currentView === 'tabletView' && (
						<Box styles={{ paddingBottom: '32px', flexDirection: 'row', gap: '48px', alignSelf: 'center' }}>
							<Box styles={{ marginTop: '34px', alignSelf: 'center', marginBottom: '18px' }}>
								<Link
									onPress={handleClickContinueToSite}
									variant="light"
									appearance="primary"
									size="large"
									middleContent={
										BaseSessionInfoLoanType.CL === creditType
											? translate('simulator.modals.autoSimulate.continueSiteCL')
											: translate('simulator.modals.autoSimulate.dontHaveApp')
									}
									web={{
										target: '_blank',
									}}
								/>
							</Box>
							<Box styles={{ alignSelf: 'self-end' }}>
								<Button
									onPress={handleClickContinueApp}
									size={'xLarge'}
									children={translate('simulator.modals.autoSimulate.continueOnTheApp')}
								/>
							</Box>
						</Box>
					)} */}
			</Box>
		</BaseModal>
	);
};
